const API = process.env.REACT_APP_API_URL || '/api';

const pendingRequests = new Map();

// Role-based token storage — SESSION-ONLY (sessionStorage):
// Tokens live only for the browser session. Closing the browser/tab auto-logs-out;
// reopening the app shows a clean login page (no stale token → no "session expired").
// A page refresh (F5) keeps you logged in — sessionStorage survives reload, not close.
//   sessionStorage → token_hr, token_employee, … + activeRole + lastRole + _cp
const ROLE_KEYS = ['superadmin', 'hr', 'employee', 'pm', 'intern', 'admin', 'itadmin', 'default'];

// One-time cleanup: remove tokens left by the OLD persistent (localStorage) model,
// so no one stays silently signed in across browser restarts after this change.
try {
  for (const r of ROLE_KEYS) localStorage.removeItem('token_' + r);
  localStorage.removeItem('activeRole');
  localStorage.removeItem('lastRole');
} catch (_) { /* ignore */ }

function getActiveRole() {
  return sessionStorage.getItem('activeRole');
}
function setActiveRole(role) {
  sessionStorage.setItem('activeRole', role);
  sessionStorage.setItem('lastRole', role);
}
function getToken() {
  const role = getActiveRole();
  if (role) return sessionStorage.getItem('token_' + role);
  // No activeRole yet → try lastRole, then any stored role token
  const last = sessionStorage.getItem('lastRole');
  if (last && sessionStorage.getItem('token_' + last)) {
    sessionStorage.setItem('activeRole', last);
    return sessionStorage.getItem('token_' + last);
  }
  for (const r of ROLE_KEYS) {
    const t = sessionStorage.getItem('token_' + r);
    if (t) { sessionStorage.setItem('activeRole', r); return t; }
  }
  return null;
}

function setToken(t, role) {
  if (role) {
    sessionStorage.setItem('token_' + role, t);
    setActiveRole(role);
  } else {
    // Fallback: decode role from JWT payload
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      const r = (payload.role || '').toLowerCase();
      sessionStorage.setItem('token_' + r, t);
      setActiveRole(r);
    } catch { sessionStorage.setItem('token_default', t); setActiveRole('default'); }
  }
}

// Persisted dashboard tab keys — cleared on logout so a fresh login always
// starts at the main dashboard (not the last page from the previous session).
const TAB_KEYS = ['hr_tab', 'pm_tab', 'ita_tab', 'intern_tab', 'emp_tab', 'sa_tab'];
function clearPersistedTabs() { TAB_KEYS.forEach((k) => sessionStorage.removeItem(k)); }

function clearToken() {
  const role = getActiveRole();
  if (role) sessionStorage.removeItem('token_' + role);
  sessionStorage.removeItem('activeRole');
  sessionStorage.removeItem('_cp');
  clearPersistedTabs();
}

function clearAllTokens() {
  for (const r of ROLE_KEYS) sessionStorage.removeItem('token_' + r);
  sessionStorage.removeItem('lastRole');
  sessionStorage.removeItem('activeRole');
  sessionStorage.removeItem('_cp');
  clearPersistedTabs();
}

async function request(path, options = {}) {
  // Public auth endpoints (login / google / forgot / reset) must NOT carry a
  // (possibly expired) token, and a 401/403 from them means "bad credentials" or
  // "no such account" — a real answer, not an expired session. Treating them as
  // session expiry would replace the real message with a generic "Session expired"
  // and redirect to /login.
  // `sso-exchange` belongs here for the same reason as the rest: it is called
  // with no session, so a 401 from it means "there is no sign-in waiting" — a
  // real answer. Treated as session expiry it would wipe every role token and
  // bounce to /login, which on the SSO landing is a loop.
  const isPublicAuth = /^\/(auth\/(login|google|forgot-password|reset-password|sso-exchange)|2fa\/verify)\b/.test(path);
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token && !isPublicAuth) headers['Authorization'] = `Bearer ${token}`;
  if (!(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';

  const requestKey = `${options.method || 'GET'}-${path}-${JSON.stringify(options.body || {}).substring(0, 100)}`;
  if (pendingRequests.has(requestKey)) return pendingRequests.get(requestKey);

  try {
    const requestPromise = (async () => {
      const res = await fetch(`${API}${path}`, { ...options, headers });
      const contentType = res.headers.get('content-type') || '';
      const text = await res.text();
      let data;
      try {
        data = text && (contentType.includes('application/json') || text.trim().startsWith('{') || text.trim().startsWith('['))
          ? JSON.parse(text) : null;
      } catch (_) {
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          throw new Error('API unreachable. Backend server may be down or wrong URL. Check REACT_APP_API_URL.');
        }
        throw new Error(text || 'Invalid server response');
      }
      // Login-screen errors (bad password, or a Google email with no active account)
      // are real answers — surface the server message, never redirect or wipe tokens.
      if ((res.status === 401 || res.status === 403) && isPublicAuth) {
        // Carry data.code so callers can tell a retryable wrong 2FA code from an
        // expired challenge (CHALLENGE_EXPIRED).
        const authErr = new Error(data?.error || (res.status === 401 ? 'Invalid credentials' : 'Access denied'));
        if (data?.code) authErr.code = data.code;
        throw authErr;
      }
      if (res.status === 401) {
        // Genuine session expiry on a protected call → clear EVERY role token (no stale
        // token left to bounce into another dashboard) and return to a clean login page.
        clearAllTokens();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
      if (res.status === 403) {
        // Org just enforced 2FA and this (non-Google) session hasn't set it up →
        // bounce once to the app root so AppRoutes re-reads /auth/me and shows the
        // setup gate. (getMe/2fa/auth are allowlisted server-side, so no loop.)
        if (data?.code === 'TWO_FACTOR_SETUP_REQUIRED' && !sessionStorage.getItem('_2fa_gate_bounced')) {
          sessionStorage.setItem('_2fa_gate_bounced', '1');
          window.location.reload();
        }
        throw new Error(data?.error || 'Access denied');
      }
      if (!res.ok) throw new Error(data?.error || data?.message || text || 'Request failed');
      return data || {};
    })();
    pendingRequests.set(requestKey, requestPromise);
    const result = await requestPromise;
    pendingRequests.delete(requestKey);
    return result;
  } catch (err) {
    pendingRequests.delete(requestKey);
    console.error(`API ${path}:`, err.message);
    throw err;
  }
}

const get = (path) => request(path);
const post = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });
const put = (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body) });
const del = (path) => request(path, { method: 'DELETE' });

const api = {
  // Auth
  login: (email, password, expectedRole) => post('/auth/login', { email, password, expectedRole }),
  getMe: () => get('/auth/me'),
  switchRole: (role) => post('/auth/switch-role', { role }),
  getUserRoles: (empId) => get(`/admin/users/${empId}/roles`),
  setUserRoles: (empId, roles, primary) => put('/admin/users/roles', { empId, roles, primary }),
  changePassword: (currentPassword, newPassword) => put('/auth/change-password', { currentPassword, newPassword }),
  forgotPassword: (email) => post('/auth/forgot-password', { email }),
  resetPassword: (email, otp, newPassword) => post('/auth/reset-password', { email, otp, newPassword }),

  // ── Sign in with Google ──
  // Exchanges the Google authorization code (from GIS) + the chosen role for a session.
  loginWithGoogle: (code, expectedRole) => post('/auth/google', { code, expectedRole }),

  // ── Sign in with AMS LoginHub (OIDC) ──
  // There is no "start SSO" call here on purpose: HRMS never initiates the
  // flow. People arrive from the portal, which sends them to /auth/login. The
  // login page offers no LoginHub button and is unchanged.
  //
  // Redeem the single-use ticket that /auth/callback left in an HttpOnly cookie.
  // `credentials: 'include'` is the whole point of the call — the cookie IS the
  // authorisation, since there is no token yet. Returns the same { token, user }
  // shape as a password login, so the caller treats it identically.
  ssoExchange: () => request('/auth/sso-exchange', { method: 'POST', credentials: 'include', body: JSON.stringify({}) }),

  // ── Two-Step Verification (TOTP) — password login only ──
  twoFactorStatus:    () => get('/2fa/status'),
  twoFactorSetup:     () => post('/2fa/setup', {}),
  twoFactorEnable:    (code) => post('/2fa/enable', { code }),
  twoFactorVerify:    (challengeToken, code) => post('/2fa/verify', { challengeToken, code }),
  // Superadmin-only: clear a user's 2FA (lost device / lockout recovery).
  twoFactorResetUser: (ref) => post('/2fa/reset-user', /^\d+$/.test(String(ref)) ? { userId: ref } : { empId: ref }),
  logout: () => { clearToken(); window.location.href = '/login'; },
  logoutAll: () => { clearAllTokens(); window.location.href = '/login'; },

  // Company overview — aggregate counts for every signed-in role. The public
  // landing page draws the same charts from representative figures; this is
  // the same picture from the real tables, which is why it needs a session.
  getOverview: () => get('/overview'),

  // Onboarding
  submitOnboarding: (formData) => request('/onboarding/submit', { method: 'POST', body: formData }),
  getOnboardingStatus: (empId) => get(`/onboarding/status${empId ? '/' + empId : ''}`),
  getPendingVerifications: () => get('/onboarding/pending'),
  verifyOnboarding: (empId, action, remarks = '', rejectedSections = []) => post('/onboarding/verify', { empId, action, remarks, rejectedSections }),
  requestResubmission: (empId, reason, rejectedSections = []) => post('/onboarding/resubmit-request', { empId, reason, rejectedSections }),
  getOnboardingStats: () => get('/onboarding/stats'),
  getChecklist: (empId) => get(`/onboarding/checklist${empId ? '/' + empId : ''}`),
  updateChecklist: (empId, label, done) => put(`/onboarding/checklist/${empId}`, { label, done }),
  deleteDocument: (docId) => del(`/onboarding/documents/${docId}`),
  getDocumentTypes: () => get('/onboarding/document-types'),

  // HR
  getHrDashboard: () => get('/hr/dashboard'),
  getHrAnalytics: (params = {}) => {
    const q = new URLSearchParams();
    if (params.hcYear) q.set('hcYear', params.hcYear);
    if (params.leaveMode) q.set('leaveMode', params.leaveMode);
    if (params.leaveValue) q.set('leaveValue', params.leaveValue);
    const qs = q.toString();
    return get(`/hr/analytics${qs ? `?${qs}` : ''}`);
  },
  getHrInterns: (search) => get(`/hr/interns${search ? '?search=' + encodeURIComponent(search) : ''}`),
  createIntern: (data) => post('/hr/intern', data),
  verifyIntern: (internId, action, reason = '') => post('/hr/intern/verify', { internId, action, reason }),
  convertInternToEmployee: (payload) => put('/hr/intern/convert', payload),
  suggestNextEmpId: (role = 'employee') => get(`/hr/next-emp-id?role=${role}`),
  updateEmployeeIdentity: (payload) => put('/hr/employee/identity', payload),
  getHrEmployees: (search) => get(`/hr/employees${search ? '?search=' + encodeURIComponent(search) : ''}`),
  createEmployee: (data) => post('/hr/employee', data),
  verifyEmployee: (empId, action, reason = '') => post('/hr/employee/verify', { empId, action, reason }),
  createCredentials: (empId, tempPassword) => post('/hr/credentials', { empId, tempPassword }),
  updateCredentials: (empId, newPassword) => put('/hr/credentials', { empId, newPassword }),
  getOffboarding: () => get('/hr/offboarding'),
  toggleUserStatus: (userId, action, reason) => post('/hr/users/toggle-status', { userId, action, reason }),
  updateOffboarding: (id, field, value) => put('/hr/offboarding', { id, field, value }),
  proceedOffboarding: (id) => post('/hr/offboarding/proceed', { id }),
  cancelOffboarding: (id, reason) => post('/hr/offboarding/cancel', { id, reason }),
  ensureOffboardingRecord: (userId) => post('/hr/offboarding/ensure', { userId }),
  getAuditLogs: () => get('/hr/audit-logs'),

  // ── Birthday stories (every role) ──
  getTodaysBirthdays: () => get('/birthdays/today'),
  markBirthdayStorySeen: (userId) => post(`/birthdays/${userId}/seen`, {}),
  sendBirthdayWish: (userId) => post(`/birthdays/${userId}/wish`, {}),
  getBirthdayWishers: (userId) => get(`/birthdays/${userId}/wishers`),

  // ── Unified Audit / Activity Log (role-scoped server-side) ──
  getActivityLogs: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') q.set(k, v); });
    const qs = q.toString();
    return get(`/audit/logs${qs ? `?${qs}` : ''}`);
  },
  getActivityFilters: () => get('/audit/filters'),
  getMyActivity: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
    const qs = q.toString();
    return get(`/audit/my${qs ? `?${qs}` : ''}`);
  },
  getAuditEntityHistory: (type, id) => get(`/audit/entity/${encodeURIComponent(type)}/${encodeURIComponent(id)}`),
  getAuditConfig: () => get('/audit/config'),
  updateAuditConfig: (body) => put('/audit/config', body),
  // Export the current filtered view as CSV (auth header required → fetch+blob).
  downloadAuditCsv: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && v !== 'all') q.set(k, v); });
    const res = await fetch(`${API}/audit/export?${q.toString()}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  // Monthly Attendance Register — download the .xlsx template (with LOCATION/GENDER/STATUS dropdowns).
  downloadAttendanceRegisterTemplate: async (month, year) => {
    const res = await fetch(`${API}/hr/attendance-register/template?month=${month}&year=${year}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Template download failed');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance-register-${year}-${String(month).padStart(2, '0')}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  getAllUsersForChat: () => get('/hr/all-users'),
  getHrStats: () => get('/hr/stats'),
  getHrReports: () => get('/hr/hr-reports'),
  getHrReportProjects: () => get('/hr/report-projects'),
  getHrPeopleDetail: (userId) => get(`/hr/people/${userId}/detail`),
  uploadHrReport: (formData) => request('/hr/reports/upload', { method: 'POST', body: formData }),
  resubmitHrReport: (reportId, formData) => request(`/hr/reports/${reportId}/resubmit`, { method: 'PUT', body: formData }),
  getHrReportLogs: (reportId) => get(`/hr/reports/${reportId}/logs`),
  getPayrollSummary: () => get('/hr/payroll-summary'),
  getAllPayslips: () => get('/hr/all-payslips'),
  sendPayrollToManager: (data) => post('/hr/send-payroll', data),

  // Salary Details — payroll LOP (leave) grid
  getPayrollLop: (month, year) => get(`/payroll-lop?month=${month}&year=${year}`),
  savePayrollLop: (data) => post('/payroll-lop/save', data),
  // Salary calculation — stored Monthly Gross per month (carry-forward); upload only to update
  getSalaryGrid: (month, year) => get(`/payroll-salary/grid?month=${month}&year=${year}`),
  getSalaryHistory: (code) => get(`/payroll-salary/history/${encodeURIComponent(code)}`),
  saveSalaryAdjustments: (data) => post('/payroll-salary/adjustments', data),
  previewSalary: (formData) => request('/payroll-salary/preview', { method: 'POST', body: formData }),
  // Salary-batch: employee bank master + bank-file generation
  getSalaryEmployees: () => get('/salary-batch/employees'),
  saveSalaryEmployee: (data) => post('/salary-batch/employees', data),
  bulkSalaryEmployees: (formData) => request('/salary-batch/employees/bulk', { method: 'POST', body: formData }),
  deleteSalaryEmployee: (code) => del(`/salary-batch/employees/${encodeURIComponent(code)}`),
  generateBankFile: (formData) => request('/salary-batch/generate?output=link', { method: 'POST', body: formData }),
  // Multipart download → returns a Blob (.xlsx); caller triggers the download.
  downloadFormBlob: async (path, formData) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    if (!res.ok) {
      let msg = 'Download failed';
      try { msg = (await res.json()).error || msg; } catch (_) { /* keep default */ }
      throw new Error(msg);
    }
    return res.blob();
  },
  // Returns a Blob (.xlsx); caller triggers the actual download.
  downloadFileBlob: async (path, body) => {
    const token = getToken();
    const res = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let msg = 'Download failed';
      try { msg = (await res.json()).error || msg; } catch (_) { /* keep default */ }
      throw new Error(msg);
    }
    return res.blob();
  },

  uploadPayslipTemplate: (formData) => request('/hr/payslip-template', { method: 'POST', body: formData }),
  getActivePayslipTemplate: () => get('/hr/payslip-template/active'),
  uploadInternCertificateTemplate: (formData) => request('/hr/intern-certificate-template', { method: 'POST', body: formData }),
  getActiveInternCertificateTemplate: () => get('/hr/intern-certificate-template/active'),
  processPayrollUpload: (formData) => request('/hr/payroll/process', { method: 'POST', body: formData }),
  getPayrollProcessLogs: () => get('/hr/payroll/process-logs'),
  getHrNotes: () => get('/hr/notes'),
  addHrNote: (data) => post('/hr/notes', data),
  updateHrNote: (id, data) => put(`/hr/notes/${id}`, data),
  deleteHrNote: (id) => del(`/hr/notes/${id}`),
  getHrSettings: () => get('/hr/settings'),
  saveHrSetting: (key, value) => put('/hr/settings', { key, value }),
  getSettlement: (empId) => get(`/hr/settlement/${empId}`),
  saveSettlement: (offboardingId, data) => put(`/hr/settlement/${offboardingId}`, data),
  getAllPermissions: () => get('/hr/permissions'),
  hrPermissionAction: (reqId, action) => put('/hr/permission-action', { reqId, action }),
  getPMs: () => get('/hr/pms'),
  getHrLostReports: () => get('/hr/lost-reports'),
  sendLostNoticeToEmployee: (data) => post('/hr/lost-report/send-to-employee', data),
  getAttendanceCounts: (date, role) => get(`/hr/attendance-counts?date=${date || ''}&role=${role || ''}`),

  // HR Daily Attendance feature
  getHrDailyAttendance: (date) => get(`/hr/attendance/daily?date=${date}`),
  saveHrDailyAttendance: (date, entries) => post('/hr/attendance/daily', { date, entries }),
  getHrMonthlyAttendance: (month, year) => get(`/hr/attendance/monthly?month=${month}&year=${year}`),
  saveHrAttendanceAdjustments: (payload) => post('/hr/attendance/adjustments', payload),
  exportHrAttendanceExcel: async (month, year) => {
    const token = getToken();
    const res = await fetch(`${API}/hr/attendance/export/excel?month=${month}&year=${year}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const monthName = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
    a.href = url; a.download = `Attendance Summary ${monthName} ${year}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  },
  getHrPeople: () => get('/hr/people'),
  getPermissionConfigHR: () => get('/hr/permission-config'),
  updatePermissionConfig: (data) => put('/hr/permission-config', data),
  createLifecycleHrRequest: (formData) => request('/lifecycle/hr/request', { method: 'POST', body: formData }),
  initiateHrOffboarding: (payload) => post('/lifecycle/hr/initiate-offboarding', payload),
  getLifecycleHrRequests: (requestType) => get(`/lifecycle/hr/requests${requestType ? `?requestType=${encodeURIComponent(requestType)}` : ''}`),
  lifecycleHrAction: (payload) => post('/lifecycle/hr/action', payload),
  publishOnboardingInstruction: (formData) => request('/lifecycle/hr/onboarding-instruction', { method: 'POST', body: formData }),

  // HR Tickets
  getHrTickets: () => get('/hr/tickets'),
  getOffboardingEligibleUsers: () => get('/hr/offboarding-eligible-users'),
  replyHrTicket: (ticketId, reply, status, action) => put('/hr/tickets/reply', { ticketId, reply, status, action }),
  hrEscalateTicketToPM: (ticketId, note) => post('/hr/tickets/escalate-to-pm', { ticketId, note }),

  // HR Bulk Operations
  bulkSalaryRevision: (revisions) => post('/hr/bulk/salary-revision', { revisions }),
  bulkStatusUpdate: (updates) => post('/hr/bulk/status-update', { updates }),
  bulkDocDownload: (empId) => get(`/hr/bulk/documents?empId=${empId || ''}`),
  downloadBulkUsersTemplate: async () => {
    const token = getToken();
    const res = await fetch(`${API}/hr/bulk/users/template`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('Failed to download template');
    return res.text();
  },
  bulkUploadUsers: async (formData) => {
    const token = getToken();
    const res = await fetch(`${API}/hr/bulk/users/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (_) { data = {}; }
    if (!res.ok) throw new Error(data?.error || data?.message || text || `Upload failed (${res.status})`);
    return data;
  },
  updateHrUserEmail: (payload) => put('/hr/users/email', payload),
  suggestCompanyEmail: (empId) => get(`/hr/employees/${empId}/email-suggest`),
  updateEmployeeStatutory: (empId, body) => put(`/hr/employees/${empId}/statutory`, body),
  updateEmployeeProfile: (empId, body) => put(`/hr/employees/${empId}/profile`, body),
  updateHrUserManager: (payload) => put('/hr/users/manager', payload),
  updateHrUserDesignation: (payload) => put('/hr/users/designation', payload),
  downloadCalendarTemplate: async () => {
    const token = getToken();
    const res = await fetch(`${API}/hr/bulk/calendar/template`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('Failed to download template');
    return res.text();
  },
  bulkUploadCalendarEvents: async (formData) => {
    const token = getToken();
    const res = await fetch(`${API}/hr/bulk/calendar/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (_) { data = {}; }
    if (!res.ok) throw new Error(data?.error || data?.message || text || `Upload failed (${res.status})`);
    return data;
  },

  // HR Policy Management
  getHrPolicies: () => get('/hr/policies'),
  createPolicy: (data) => post('/hr/policies', data),
  createPolicyWithFile: (formData) => {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API}/hr/policies`, { method: 'POST', headers, body: formData })
      .then(async (r) => {
        const text = await r.text();
        let d = {};
        try { d = text ? JSON.parse(text) : {}; } catch (_) { d = { error: text || 'Upload failed' }; }
        if (!r.ok || d.error) throw new Error(d.error || d.message || `Upload failed (${r.status})`);
        return d;
      });
  },
  updatePolicyHR: (id, data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      const token = getToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(`${API}/hr/policies/${id}`, { method: 'PUT', headers, body: data })
        .then(async (r) => {
          const text = await r.text();
          let d = {};
          try { d = text ? JSON.parse(text) : {}; } catch (_) { d = { error: text || 'Update failed' }; }
          if (!r.ok || d.error) throw new Error(d.error || d.message || `Update failed (${r.status})`);
          return d;
        });
    }
    return put(`/hr/policies/${id}`, data);
  },
  deletePolicyHR: (id) => del(`/hr/policies/${id}`),
  getPerformanceCycles: () => get('/hr/performance/cycles'),
  createPerformanceCycle: (data) => post('/hr/performance/cycles', data),
  updatePerformanceCycle: (cycleId, data) => put(`/hr/performance/cycles/${cycleId}`, data),
  deletePerformanceCycle: (cycleId) => del(`/hr/performance/cycles/${cycleId}`),
  getPerformanceReviews: (cycleId) => get(`/hr/performance/reviews/${cycleId}`),
  updatePerformanceReview: (reviewId, data) => put(`/hr/performance/reviews/${reviewId}`, data),

  // HR Leave (hierarchy)
  getHrLeaveRequests: () => get('/hr/leave-requests'),
  hrLeaveAction: (reqId, action, comment) => put('/hr/leave-action', { reqId, action, comment }),
  getLeaveConfig: () => get('/hr/leave-config'),
  updateLeaveConfig: (configs) => put('/hr/leave-config', { configs }),
  getLeavePolicy: () => get('/hr/leave-policy'),
  updateLeavePolicy: (cl_cf_expiry_month) => put('/hr/leave-policy', { cl_cf_expiry_month }),
  validateAttendanceRegister: (fd) => request('/hr/attendance-register/validate', { method: 'POST', body: fd }),
  importAttendanceRegister: (fd) => request('/hr/attendance-register/import', { method: 'POST', body: fd }),
  // Staged flow: stage (store + preview, no writes) → commit (apply staged → real tables)
  stageAttendanceRegister: (fd) => request('/hr/attendance-register/stage', { method: 'POST', body: fd }),
  commitAttendanceRegister: (importId) => post('/hr/attendance-register/commit', { importId }),
  getAudienceList: () => get('/hr/audience-list'),

  // Admin
  getAdminUsers: () => get('/admin/users'),

  // PM
  getPMDashboard: () => get('/pm/dashboard'),
  getPMProjects: () => get('/pm/projects'),
  getPMProjectById: (projectId) => get(`/pm/projects/${projectId}`),
  createPMProject: (data) => post('/pm/projects', data),
  updatePMProject: (projectId, data) => put(`/pm/projects/${projectId}`, data),
  deletePMProject: (projectId) => del(`/pm/projects/${projectId}`),
  getPMAvailableMembers: (projectId) => get(`/pm/projects/${projectId}/available-members`),
  addPMProjectMember: (projectId, data) => post(`/pm/projects/${projectId}/members`, data),
  addPMProjectMembersBulk: (projectId, members) => post(`/pm/projects/${projectId}/members/bulk`, { members }),
  removePMProjectMember: (projectId, userId) => del(`/pm/projects/${projectId}/members/${userId}`),
  updatePMProjectMemberProgress: (projectId, userId, data) => put(`/pm/projects/${projectId}/members/${userId}/progress`, data),
  getPMTeamMembers: () => get('/pm/team'),
  addPMReview: (data) => post('/pm/review', data),
  getPMMemberReviews: (userId) => get(`/pm/reviews/${userId}`),
  getPMLeaveRequests: () => get('/pm/leave-requests'),
  handlePMLeaveAction: (data) => put('/pm/leave-action', data),
  getPMDocs: () => get('/pm/docs'),
  getPMDocById: (docId) => get(`/pm/docs/${docId}`),
  handlePMDocAction: (data) => put('/pm/doc-action', data),
  getPMDocUrl: (docId) => get(`/pm/docs/${docId}/url`),
  getPMAnnouncements: () => get('/pm/announcements'),
  createPMAnnouncement: (data) => post('/pm/announcements', data),
  updatePMAnnouncement: (id, data) => put(`/pm/announcements/${id}`, data),
  deletePMAnnouncement: (id) => del(`/pm/announcements/${id}`),
  getPMReports: () => get('/pm/reports'),
  getPMReportLogs: (reportId) => get(`/pm/reports/${reportId}/logs`),
  handlePMReportAction: (data) => put('/pm/reports/action', data),
  getPMSchedules: () => get('/pm/schedules'),
  createPMSchedule: (data) => post('/pm/schedules', data),
  updatePMSchedule: (scheduleId, data) => put(`/pm/schedules/${scheduleId}`, data),
  getPMInterns: () => get('/pm/interns'),
  assignPMIntern: (data) => post('/pm/interns/assign', data),
  downloadInternCertificate: async (filename) => {
    const token = getToken();
    const res = await fetch(`${API}/employee/intern-certificate`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const text = await res.text();
    if (!res.ok) {
      let msg = text;
      try { msg = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch (_) {}
      throw new Error(msg || 'Download failed');
    }
    const ct = res.headers.get('content-type') || 'text/html;charset=utf-8';
    const blob = new Blob([text], { type: ct });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'Internship_Certificate.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  createPMTask: (data) => post('/pm/tasks', data),
  updatePMTaskStatus: (taskId, data) => put(`/pm/tasks/${taskId}`, data),
  getPMPayrollReport: (reportId) => get(`/pm/reports/payroll/${reportId}`),
  verifyPMPayroll: (data) => put('/pm/reports/payroll/verify', data),

  // PM Leave hierarchy
  getPmLeaveRequests: () => get('/leave/pm-requests'),
  pmLeaveAction: (reqId, action, comment) => put('/leave/pm-action', { reqId, action, comment }),

  // PM Permissions (Fix 7)
  getPMPermissionRequests: () => get('/pm/permission-requests'),
  handlePMPermission: (reqId, action, comment) => put('/pm/permission-action', { reqId, action, comment }),

  // PM Notes (Fix 5)
  getPMNotes: () => get('/pm/notes'),
  addPMNote: (data) => post('/pm/notes', data),
  updatePMNote: (id, data) => put(`/pm/notes/${id}`, data),
  deletePMNote: (id) => del(`/pm/notes/${id}`),

  // PM People (Fix 5 & 12)
  getPMPeople: () => get('/pm/people'),
  getPMPeopleDetail: (userId) => get(`/pm/people/${userId}/detail`),
  // PM takes over an employee as their manager (reason is required)
  pmClaimEmployee: (userId, reason) => post(`/pm/employees/${userId}/claim`, { reason }),

  // PM Notifications (Fix 5 & 8)
  getPMNotifications: () => get('/pm/notifications'),
  markPMNotificationRead: (id) => put(`/pm/notifications/${id}/read`, {}),
  getPMTickets: () => get('/pm/tickets'),
  getPMLostReports: () => get('/pm/lost-reports'),
  pmSubmitLostFine: (data) => put('/pm/lost-report/comment', data),
  pmTicketAction: (ticketId, action, comment) => put('/pm/tickets/action', { ticketId, action, comment }),
  pmRespondToHandler: (ticketId, response) => put('/pm/tickets/respond-to-handler', { ticketId, response }),
  getPMHrRequests: () => get('/lifecycle/pm/requests'),
  pmHrRequestAction: (payload) => post('/lifecycle/pm/action', payload),
  getLifecycleLogs: (reqId) => get(`/lifecycle/logs/${reqId}`),

  // Chat (ALL users)
  getChannels: () => get('/chat/channels'),
  getMessages: (channelId) => get(`/chat/messages/${channelId}`),
  sendMessage: (channelId, message, fileUrl, fileName) => post('/chat/send', { channelId, message, fileUrl, fileName }),
  createChannel: (name, type, memberIds) => post('/chat/channel', { name, type, memberIds }),
  markChannelRead: (channelId) => post(`/chat/channels/${channelId}/read`, {}),
  getPresence: () => get('/chat/presence'),
  pinChannel: (channelId, pinned) => post(`/chat/channels/${channelId}/pin`, { pinned }),
  muteChannel: (channelId, muted) => post(`/chat/channels/${channelId}/mute`, { muted }),
  renameChannel: (channelId, name) => put(`/chat/channels/${channelId}`, { name }),
  setMemberRole: (channelId, userId, role) => put(`/chat/channels/${channelId}/members/${userId}/role`, { role }),
  forwardMessage: (messageId, channelIds) => post(`/chat/messages/${messageId}/forward`, { channelIds }),
  sendMessageWithReply: (channelId, message, replyToId, fileUrl, fileName) =>
    post('/chat/send', { channelId, message, replyToId, fileUrl, fileName }),
  deleteMessage: (messageId) => del(`/chat/messages/${messageId}`),
  editMessage: (messageId, message) => put(`/chat/messages/${messageId}`, { message }),
  leaveChannel: (channelId) => del(`/chat/channel/${channelId}/leave`),
  getGroupMembers: (channelId) => get(`/chat/channel/${channelId}/members`),
  addMemberToGroup: (channelId, userId) => post(`/chat/channel/${channelId}/members`, { userId }),
  removeMemberFromGroup: (channelId, userId) => del(`/chat/channel/${channelId}/members/${userId}`),
  getChatUsers: () => get('/chat/users'),
  getOrCreateDM: (targetUserId) => post('/chat/dm', { targetUserId }),
  uploadChatFile: (formData) => request('/chat/upload', { method: 'POST', body: formData }),
  // downloadChatFile: alias for downloadFile — used by chat file download buttons
  downloadChatFile: (url, filename) => api.downloadFile(url, filename),

  // Attendance
  getDailyAttendance: (month, year) => get(`/attendance/daily?month=${month}&year=${year}`),
  getMonthlyAttendance: () => get('/attendance/monthly'),
  getMonthStats: (month, year) => get(`/attendance/month-stats?month=${month}&year=${year}`),
  getAttendanceSummary: () => get('/attendance/summary'),
  checkIn: () => post('/attendance/check-in', {}),
  checkOut: () => post('/attendance/check-out', {}),
  getAllAttendance: (date) => get(`/attendance/all?date=${date}`),
  uploadAttendanceCsv: (formData) => request('/attendance/upload-csv', { method: 'POST', body: formData }),
  getAttendanceMonthlyLogs: (role) => get(`/attendance/monthly-logs${role ? `?role=${encodeURIComponent(role)}` : ''}`),
  getEmployeeListForAttendance: () => get('/attendance/employee-list'),
  getEmployeeMonthlyAttendance: (empId, month, year) => get(`/attendance/employee-monthly?empId=${encodeURIComponent(empId)}&month=${month}&year=${year}`),
  getAllEmployeesMonthly: (month, year, role) => get(`/attendance/all-monthly?month=${month}&year=${year}&role=${role||''}`),
  downloadAttendanceMonthlyCsv: async (month, year, role) => {
    const token = getToken();
    const q = new URLSearchParams();
    if (month) q.set('month', String(month));
    if (year) q.set('year', String(year));
    if (role) q.set('role', String(role));
    const res = await fetch(`${API}/attendance/monthly-export?${q.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to download monthly attendance');
    }
    return res.blob();
  },

  // Leave
  getLeaveBalance: () => get('/leave/balance'),
  sandwichCheck: (from, to) => get(`/leave/sandwich-check?from=${from}&to=${to}`),
  getMyLeaveRequests: () => get('/leave/my-requests'),
  applyLeave: (data) => post('/leave/apply', data),
  editLeave: (reqId, data) => put(`/leave/my-requests/${reqId}`, data),
  cancelLeave: (reqId) => put(`/leave/my-requests/${reqId}/cancel`, {}),
  // Employee raises a cancellation request for an already-approved leave (HR finalises)
  // dates: the days being handed back (YYYY-MM-DD); omitted → every day still ahead.
  requestLeaveCancellation: (reqId, reason, dates) => put(`/leave/my-requests/${reqId}/cancel-request`, { reason, dates }),
  // HR cancels an employee's approved leave from Mark Attendance
  hrCancelLeaveAttendance: (data) => post('/hr/attendance/cancel-leave', data),
  applyPermission: (data) => post('/leave/permission', data),
  editPermission: (reqId, data) => put(`/leave/my-permissions/${reqId}`, data),
  cancelPermission: (reqId) => put(`/leave/my-permissions/${reqId}/cancel`, {}),
  getAllLeaveRequests: () => get('/leave/all'),
  leaveAction: (reqId, action, comment) => put('/leave/action', { reqId, action, comment }),

  // Payroll
  getMyPayslips: () => get('/payroll/my-payslips'),
  getPayrollCycles: () => get('/payroll/cycles'),
  getAdvances: () => get('/payroll/advances'),
  requestAdvance: (data) => post('/payroll/advance', typeof data === 'object' ? data : { amount: data }),
  advanceAction: (advId, action) => put('/payroll/advance/action', { advId, action }),

  // Documents
  uploadDocument: (formData) => request('/documents/upload', { method: 'POST', body: formData }),
  uploadDocumentForUser: (userId, userEmpId, docType, file) => {
    const fd = new FormData();
    if (userId) fd.append('userId', String(userId));
    if (userEmpId) fd.append('userEmpId', String(userEmpId));
    if (docType) fd.append('docType', docType);
    if (file) fd.append('file', file);
    return request('/documents/upload', { method: 'POST', body: fd });
  },
  getMyDocs: () => get('/documents/my-docs'),
  getDocUrl: (id) => get(`/documents/${id}/url`),
  downloadDocById: (id, filename) => {
    return api.getDocUrl(id).then((meta) => {
      if (!meta?.url) throw new Error('Download URL not available');
      return api.downloadFile(meta.url, filename || meta.name || 'document');
    });
  },
  previewDocById: (id) => {
    return api.getDocUrl(id).then((meta) => {
      if (!meta?.url) throw new Error('Preview URL not available');
      return api.previewFile(meta.url, meta.name || 'document');
    });
  },
  // Submit all draft documents for HR review (locks them).
  submitMyDocuments: () => post('/documents/submit', {}),
  // Fetch a document as a blob URL for INLINE rendering (thumbnails/embeds),
  // without opening the global preview modal. Caller must revokeObjectURL.
  getDocBlobUrl: async (id) => {
    const meta = await api.getDocUrl(id);
    if (!meta?.url) throw new Error('Preview URL not available');
    const filename = meta.name || 'document';
    const fetchUrl = api.buildProxyUrl(meta.url, filename, 'inline');
    const token = getToken();
    const res = await fetch(fetchUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('Preview failed');
    const blob = await res.blob();
    return { blobUrl: window.URL.createObjectURL(blob), mimeType: blob.type, filename };
  },
  getDocument: (id) => get(`/documents/${id}`),
  deleteDoc: (id) => del(`/documents/${id}`),
  reviewDoc: (id, status, comment) => put(`/documents/${id}/review`, { status, comment }),
  getUserDocs: (userId) => get(`/documents/user/${userId}`),

  // Calendar & Events
  getEvents: (month, year) => get(`/calendar/events?month=${month}&year=${year}`),
  createEvent: (data) => post('/calendar/events', data),
  getNotifications: () => get('/calendar/notifications'),
  markNotificationRead: (id) => put(`/calendar/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => put('/calendar/notifications/read-all', {}),
  getAnnouncements: () => get('/calendar/announcements'),
  createAnnouncement: (data) => post('/calendar/announcements', data),
  // Amend or call off one that has already been sent — see calendarController.
  updateAnnouncement: (annId, data) => put(`/calendar/announcements/${annId}`, data),
  // No body: del() does not carry one, and cancelling always tells people.
  cancelAnnouncement: (annId) => del(`/calendar/announcements/${annId}`),

  // Calendar Reminders
  getReminders: () => get('/calendar/reminders'),
  createReminder: (data) => post('/calendar/reminders', data),
  deleteReminder: (id) => del(`/calendar/reminders/${id}`),
  checkReminders: () => post('/calendar/check-reminders', {}),

  // Search
  globalSearch: (q) => get(`/search?q=${encodeURIComponent(q)}`),

  // Profile
  getProfile: () => get('/profile/me'),
  getProfileById: (empId) => get(`/profile/${empId}`),
  updateProfile: (data) => put('/profile/me', data),
  uploadAvatar: (formData) => request('/profile/avatar', { method: 'POST', body: formData }),
  removeAvatar: () => del('/profile/avatar'),

  // Employee
  getTimeline: () => get('/employee/timeline'),
  getTickets: () => get('/employee/tickets'),
  createTicket: (data) => post('/employee/tickets', data),
  escalateTicket: (data) => post('/employee/tickets/escalate', data),
  getEscalablePMs: () => get('/employee/pms-list'),
  getMyLostNotices: () => get('/employee/lost-notices'),
  getEIDCardStatus: () => get('/employee/eid-card-status'),
  acknowledgeLostNotice: (noticeId) => post('/employee/lost-notices/ack', { noticeId }),
  getMyProjects: () => get('/employee/my-projects'),
  getMyProjectDetail: (projectId) => get(`/employee/my-projects/${projectId}`),
  getTicket: (id) => get(`/employee/tickets/${id}`),
  updateTicket: (id, data) => put(`/employee/tickets/${id}`, data),
  deleteTicket: (id) => del(`/employee/tickets/${id}`),
  getPolicies: () => get('/employee/policies'),
  downloadPolicy: (id) => get(`/employee/policies/${id}/download`),
  // Reading a policy in the app — allowed even when it may not be saved.
  viewPolicy: (id) => get(`/employee/policies/${id}/view`),
  getAssets: () => get('/employee/assets'),
  getNotes: () => get('/employee/notes'),
  addNote: (data) => post('/employee/notes', data),
  updateNote: (id, data) => put(`/employee/notes/${id}`, data),
  deleteNote: (id) => del(`/employee/notes/${id}`),
  getPermissionConfig: () => get('/employee/permission-config'),
  getPermissionBalance: () => get('/leave/permission-balance'),
  getMyPermissions: () => get('/leave/my-permissions'),
  getLeaveCalendarData: (month, year) => get(`/leave/calendar-data?month=${month}&year=${year}`),
  getLeaveTypes: () => get('/leave/types'),
  getPeopleDirectory: () => get('/employee/people'),
  getExperience: () => get('/employee/experience'),
  getMyPerformance: () => get('/employee/my-performance'),
  getMyAdvances: () => get('/payroll/my-advances'),
  submitEmployeeOffboarding: (formData) => request('/lifecycle/employee/offboarding-submit', { method: 'POST', body: formData }),
  submitEmployeeOffboardingForm: (payload) => post('/lifecycle/employee/offboarding-form', payload),
  getEmployeeLifecycleRequests: (requestType) => get(`/lifecycle/employee/requests${requestType ? `?requestType=${encodeURIComponent(requestType)}` : ''}`),
  getActiveOnboardingInstruction: () => get('/lifecycle/employee/onboarding-instruction'),
  submitEmployeeLifecycleSign: (formData) => request('/lifecycle/employee/sign', { method: 'POST', body: formData }),
  submitEmployeeOffboardingAssets: (payload) => post('/lifecycle/employee/offboarding-assets', payload),
  getITOffboardingQueue: () => get('/lifecycle/it/offboarding-queue'),
  itReplyOffboarding: (payload) => post('/lifecycle/it/offboarding-reply', payload),
  hrUploadOffboardingDocs: (formData) => request('/lifecycle/hr/offboarding-docs', { method: 'POST', body: formData }),
  hrSendOffboardingSettlement: (payload) => post('/lifecycle/hr/offboarding-settlement-send', payload),

  // IT Admin
  getITDashboard: () => get('/itadmin/dashboard'),
  getITAssets: () => get('/itadmin/assets'),
  getITAdminsList: () => get('/itadmin/itadmins-list'),
  getITOnboardingQueue: () => get('/itadmin/onboarding-queue'),
  createITAsset: (data) => post('/itadmin/assets', data),
  updateITAsset: (data) => put('/itadmin/assets', data),
  deleteITAsset: (assetId) => del(`/itadmin/assets/${assetId}`),
  bulkUploadAssets: async (file) => {
    const token = getToken();
    // Read CSV as text and send as JSON — no multer needed
    const csvContent = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
    const res = await fetch(`${API}/itadmin/assets/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ csvContent, fileName: file.name })
    });
    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Server error: ' + text.slice(0, 100)); }
    if (!res.ok) throw new Error(data?.error || 'Upload failed');
    return data;
  },
  bulkUploadCredentials: async (file) => {
    const token = getToken();
    const csvContent = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
    const res = await fetch(`${API}/itadmin/credentials/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ csvContent, fileName: file.name })
    });
    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Server error: ' + text.slice(0, 100)); }
    if (!res.ok) throw new Error(data?.error || 'Upload failed');
    return data;
  },
  getITCredentials: () => get('/itadmin/credentials'),
  addITCredential: (data) => post('/itadmin/credentials', data),
  updateITCredential: (data) => put('/itadmin/credentials', data),
  deleteITCredential: (userId) => del(`/itadmin/credentials/${userId}`),
  getITPeople: () => get('/itadmin/people'),
  getITRequests: () => get('/itadmin/requests'),
  createITRequest: (data) => post('/itadmin/requests', data),
  itRequestAction: (reqId, action, remarks) => put('/itadmin/requests/action', { reqId, action, remarks }),
  itEscalateTicketToPM: (ticketId, note) => post('/itadmin/tickets/escalate-to-pm', { ticketId, note }),
  getITExitChecklist: (empId) => get(`/itadmin/exit-checklist?empId=${empId || ''}`),
  getExitChecklist: (empId) => get(`/itadmin/exit-checklist?empId=${empId || ''}`),
  updateITExitChecklist: (data) => put('/itadmin/exit-checklist', data),
  updateExitItem: (data) => put('/itadmin/exit-checklist', data),
  completeITExit: (data) => post('/itadmin/exit-complete', data),
  completeExitChecklist: (data) => post('/itadmin/exit-complete', data),
  getITExitHistory: () => get('/itadmin/exit-history'),
  getExitHistory: (month, year) => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (year)  params.set('year', year);
    const qs = params.toString();
    return get(`/itadmin/exit-history${qs ? '?' + qs : ''}`);
  },
  downloadExitCSV: (month, year) => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (year)  params.set('year', year);
    const qs = params.toString();
    return `/itadmin/exit-history/csv${qs ? '?' + qs : ''}`;
  },
  getITLogs: () => get('/itadmin/logs'),
  getITNotes: () => get('/itadmin/notes'),
  addITNote: (data) => post('/itadmin/notes', data),
  updateITNote: (id, data) => put(`/itadmin/notes/${id}`, data),
  deleteITNote: (id) => del(`/itadmin/notes/${id}`),
  // Software Licenses
  getITLicenses: () => get('/itadmin/licenses'),
  addITLicense: (data) => post('/itadmin/licenses', data),
  updateITLicense: (data) => put('/itadmin/licenses', data),
  deleteITLicense: (id) => del(`/itadmin/licenses/${id}`),
  // AI Tools
  getITAITools: () => get('/itadmin/ai-tools'),
  addITAITool: (data) => post('/itadmin/ai-tools', data),
  updateITAITool: (data) => put('/itadmin/ai-tools', data),
  deleteITAITool: (id) => del(`/itadmin/ai-tools/${id}`),
  // Employee asset lookup

  // ── LOP ──────────────────────────────────────────────────
  /* The /api/lop namespace was never mounted — the LOP tools live under
     /api/payroll-lop and are HR-only — so every method here answered 404. They
     are gone rather than left as traps; an employee-facing LOP view would need
     an endpoint of its own. */
  getEmployeeAssets: (empId) => get(`/itadmin/employee-assets/${empId}`),
  // Warranty management
  getWarrantyAssets: () => get('/itadmin/warranty-assets'),
  markWarrantyDone: (assetId) => put('/itadmin/warranty-done', { assetId }),
  // Maintenance update
  updateMaintenance: (assetId, nextMaintenanceDate) => put('/itadmin/maintenance', { assetId, nextMaintenanceDate }),
  // Lost asset reports (IT Admin)
  createLostReport: (data) => post('/itadmin/lost-report', data),
  getITLostReports: () => get('/itadmin/lost-reports'),
  getLostReportById: (reportId) => get(`/itadmin/lost-reports/${reportId}`),
  // Lost asset reports (PM)
  getPMLostReports: () => get('/pm/lost-reports'),
  // IT Admin Announcements
  getITAnnouncements: () => get('/itadmin/announcements'),
  createITAnnouncement: (data) => post('/itadmin/announcements', data),
  getITProjectsList: () => get('/itadmin/projects-list'),
  createAnnEvaluation: (data) => post('/itadmin/announcements/evaluate', data),
  getAnnEvaluations: (annId) => get(`/itadmin/announcements/${annId}/evaluations`),
  // PM Evaluations
  getPMEvaluations: () => get('/itadmin/evaluations/pm'),
  pmReplyEvaluation: (evalId, reply) => put('/itadmin/evaluation/reply', { evalId, reply }),
  pmCommentLostReport: (reportId, comment) => put('/pm/lost-report/comment', { reportId, comment }),
  // Departments
  getDepartments: () => get('/hr/departments'),
  createDepartment: (data) => post('/hr/departments', data),
  updateDepartment: (data) => put('/hr/departments', data),
  deleteDepartment: (id) => del(`/hr/departments/${id}`),
    // Certificate workflow
  getCertificateRequests: () => get('/hr/certificate-requests'),
  sendCertificateToPM: (formData) => request('/hr/certificate-requests/send-pm', { method: 'POST', body: formData }),
  sendCertificateToIntern: (requestId) => post('/hr/certificate-requests/send-intern', { requestId }),
getPMCertificateRequests: () => get('/pm/certificate-requests'),
  handlePMCertificateAction: (data) => put('/pm/certificate-requests/action', data),
getHRCertificatePreviewUrl: (requestId) => `${API}/hr/certificate-requests/${requestId}/preview`,
getPMCertificatePreviewUrl: (requestId) => `${API}/pm/certificate-requests/${requestId}/preview`,
getInternCertificatePreviewUrl: () => `${API}/employee/intern-certificate/preview`,
checkCertificateReady: () => get('/employee/certificate-ready'),
  // Super Admin
  getAdminOverview: () => get('/admin/overview'),
  getAdminStats: () => get('/admin/stats'),
  updatePermissions: (roleId, moduleId, level) => put('/admin/permissions', { roleId, moduleId, level }),
  bulkUpdatePermissions: (permissions) => put('/admin/permissions/bulk', { permissions }),
  createUser: (data) => post('/admin/users', data),
  changeUserRole: (empId, newRole) => put('/admin/users/role', { empId, newRole }),
  updateUserStatus: (empId, status) => put('/admin/users/status', { empId, status }),
  resetUserPassword: (empId, newPassword) => put('/admin/users/password', { empId, newPassword }),
  deleteUser: (empId) => del(`/admin/users/${empId}`),
  updateSecurity: (key, value) => put('/admin/security', { key, value }),
  getAdminAuditLog: () => get('/admin/audit-log'),
  getAdminNotifications: () => get('/admin/notifications'),
  markAdminNotificationRead: (id) => put(`/admin/notifications/${id}/read`, {}),
  markAllAdminNotificationsRead: () => put('/admin/notifications/read-all', {}),
  // Module Management
  getAdminLeaveRequests: () => get('/admin/leave-requests'),
  updateAdminLeaveRequest: (id, status, comment) => put('/admin/leave-requests', { id, status, comment }),
  getAdminAttendance: (date) => get(`/admin/attendance?date=${date || ''}`),
  getAdminPayslips: () => get('/admin/payslips'),
  updateAdminPayslip: (data) => put('/admin/payslips', data),
  getAdminProjects: () => get('/admin/projects'),
  updateAdminProject: (data) => put('/admin/projects', data),
  deleteAdminProject: (id) => del(`/admin/projects/${id}`),
  getAdminTickets: () => get('/admin/tickets'),
  updateAdminTicket: (data) => put('/admin/tickets', data),
  getAdminPolicies: () => get('/admin/policies'),
  updateAdminPolicy: (data) => put('/admin/policies', data),
  deleteAdminPolicy: (id) => del(`/admin/policies/${id}`),
  getAdminPermissions: () => get('/admin/permissions-requests'),
  getAdminAssets: () => get('/admin/assets'),
  getAdminEvents: () => get('/admin/events'),
  deleteAdminEvent: (id) => del(`/admin/events/${id}`),
  deleteAdminAnnouncement: (id) => del(`/admin/announcements/${id}`),
  broadcastNotification: (data) => post('/admin/broadcast', data),

  // Utilities
  setToken, getToken, clearToken, clearAllTokens, getActiveRole,
  isAuthenticated: () => !!getToken(),
  healthCheck: () => get('/health'),

  resolveFileUrl: (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const apiBase = API.startsWith('http')
      ? API.replace('/api', '')
      : (window.location.origin.includes(':3000')
        ? window.location.origin.replace(':3000', ':5000')
        : window.location.origin.includes(':4001')
          ? window.location.origin.replace(':4001', ':4000')
        : window.location.origin);
    return `${apiBase}${url}`;
  },

  openFile: async (url) => {
    // Route through previewFile so it stays in the same tab (in-app modal).
    await api.previewFile(url, '');
  },

   buildProxyUrl: (urlOrKey, filename, disposition = 'attachment') => {
    const proxyBase = `${API}/documents/proxy`;
    const params = new URLSearchParams({
      url: urlOrKey, // the S3 key travels as-is; the backend resolves and signs it
      filename: filename || 'file',
      disposition,
    });
    return `${proxyBase}?${params.toString()}`;
  },

  downloadFile: async (url, filename) => {
    try {
      if (!url) throw new Error('Invalid file path');
      
      // Route the key through the backend proxy so the signed URL is never exposed here.
      const fetchUrl = api.buildProxyUrl(url, filename, 'attachment');

      const token = getToken();
      const response = await fetch(fetchUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Server error');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'file';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert("Download Error: " + error.message);
    }
  },

  // allowDownload: false is for documents that may be read but not kept — the
  // preview then offers no way out to a file. See EmpPoliciesPage.
  previewFile: async (url, filename = 'file', { allowDownload = true } = {}) => {
  try {
    const fetchUrl = api.buildProxyUrl(url, filename, 'inline');
    const token = getToken();
    const response = await fetch(fetchUrl, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });

    if (!response.ok) throw new Error('Preview failed');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob); 
    
    window.dispatchEvent(new CustomEvent('openFilePreview', {
      detail: {
        blobUrl,
        filename,
        mimeType: blob.type,
        allowDownload
      }
    }));
  } catch (error) {
    console.error(error);
    throw error;
  }
}

};
export default api;